/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { VIEW_TYPES } from 'components/common/view-type/settings'
import { DISPOSAL_TICKET_STATUS } from 'containers/billing/review/disposal-tickets/settings'
import { DATA_GRID_DEFAULT_PINNED_COLUMNS } from 'settings/constants/dataGrid'

import { SerializedDateRange } from './persistentReviewDateRangeTypes'

const { UNMATCHED } = DISPOSAL_TICKET_STATUS
const { LIST } = VIEW_TYPES

type PinnedColumns = typeof DATA_GRID_DEFAULT_PINNED_COLUMNS

interface PanelSizes {
  workOrderPanel: number
  disposalTicketPanel: number
}

export interface PersistentDisposalTicketsReviewState {
  selectedTab: string
  viewType: string
  pinnedColumns: PinnedColumns
  isWorkOrderPanelOpen: boolean
  panelSizes: PanelSizes
  dateRange: SerializedDateRange | null
}

const initialState: PersistentDisposalTicketsReviewState = {
  selectedTab: UNMATCHED.value,
  viewType: LIST,
  pinnedColumns: { ...DATA_GRID_DEFAULT_PINNED_COLUMNS },
  isWorkOrderPanelOpen: true,
  panelSizes: {
    workOrderPanel: 40,
    disposalTicketPanel: 60,
  },
  dateRange: null,
}

const disposalTicketsReviewSlice = createSlice({
  name: 'persistentDisposalTicketsReview',
  initialState: { ...initialState },
  reducers: {
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload
    },
    setViewType: (state, action: PayloadAction<string>) => {
      state.viewType = action.payload
    },
    setPinnedColumns: (state, action: PayloadAction<PinnedColumns>) => {
      state.pinnedColumns = action.payload
    },
    setIsWorkOrderPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isWorkOrderPanelOpen = action.payload
    },
    setPanelSizes: (state, action: PayloadAction<PanelSizes>) => {
      state.panelSizes = action.payload
    },
    setDateRange: (state, action: PayloadAction<SerializedDateRange | null>) => {
      state.dateRange = action.payload
    },
    resetState: () => initialState,
  },
})

export const { setSelectedTab, setViewType, setPinnedColumns, setIsWorkOrderPanelOpen, setPanelSizes, setDateRange, resetState } =
  disposalTicketsReviewSlice.actions

// The persisted slice can predate a key, so every selector falls back to the initial value.
interface StateWithDisposalTicketsReview {
  persistentDisposalTicketsReview?: Partial<PersistentDisposalTicketsReviewState>
}

export const selectSelectedTab = (state: StateWithDisposalTicketsReview): string =>
  state.persistentDisposalTicketsReview?.selectedTab ?? UNMATCHED.value
export const selectViewType = (state: StateWithDisposalTicketsReview): string => state.persistentDisposalTicketsReview?.viewType ?? LIST
export const selectPinnedColumns = (state: StateWithDisposalTicketsReview): PinnedColumns =>
  state.persistentDisposalTicketsReview?.pinnedColumns ?? initialState.pinnedColumns
export const selectIsWorkOrderPanelOpen = (state: StateWithDisposalTicketsReview): boolean =>
  state.persistentDisposalTicketsReview?.isWorkOrderPanelOpen ?? initialState.isWorkOrderPanelOpen
export const selectPanelSizes = (state: StateWithDisposalTicketsReview): PanelSizes =>
  state.persistentDisposalTicketsReview?.panelSizes ?? initialState.panelSizes
export const selectDateRange = (state: StateWithDisposalTicketsReview): SerializedDateRange | null =>
  state.persistentDisposalTicketsReview?.dateRange ?? null

export default disposalTicketsReviewSlice.reducer
